import { useState, useEffect, useMemo } from "react";
import "./deepSearch.css";
import Select from "react-select";
import mainAxios from "../../../redux/api/mainAxios";
import { useDispatch, useSelector } from "react-redux";
import {
  deepSearchFunction,
  getSearchResultsCount,
} from "../../../redux/actions/immobilierActions";
import moment from "moment";
import { redirect } from "react-router-dom";
import { closeModal, openModal } from "../../../redux/actions/modalActions";
import toast from "react-hot-toast";
import { UserData } from "../../../redux/actions/userAuthActions";
import debounce from "../../../utils/debounce";
import { fetchInitialData } from "../../../redux/actions/deepsearchActions";
import {
  getMultiSelectValue,
  handleMultiSelectChange,
  customStyles,
  createPaginationState,
  getVisibleOptions,
  handleScrollToBottom,
  handleInputChange,
} from "./deepsearchUtils";
import EditSearchModal from "../../enregistres/components/EditSearchModal";

const DeepSearch = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.userAuthReducer);
  const {
    lieu,
    promoteur,
    programme,
    num_lot,
    dispositif,
    status_lot,
    zone_fiscal,
    loading,
  } = useSelector((state) => state.deepsearchReducer);

  const [formData, setFormData] = useState({
    // General section
    nomRecherche: "",
    usage: null,
    lieu: [],
    dispositif: [],
    zone_fiscale: [],
    statutLot: "",
    origine: null,
    perimetere: null,
    rayonOptionType: null, // To track if "Selectionner..." is selected
    // Economic section
    budgetMin: null,
    budgetMax: null,
    prixM2Min: null,
    prixM2Max: null,
    rentabiliteMin: null,
    honorairesMin: null,
    // Lot section
    referenceLot: [],
    type: [],
    surfaceMin: null,
    surfaceMax: null,
    espaceTotalMin: null,
    espaceTotalMax: null,
    typologieLot: [],
    etage: [],
    annexe: [],
    // Programme section
    programme: [],
    programmeHidden: false,
    promoteur: [],
    // tag: null,
    label: [],
    nouveaute_depuis: null,
    lot_maximum: null,
    allotement: false,
    offre_speciale: false,
    missingLotData: false,
  });

  useEffect(() => {
    dispatch(fetchInitialData());
  }, [dispatch]);

  useEffect(() => {
    if (
      formData.lieu &&
      formData.lieu.length === 1 &&
      formData.perimetere === null
    ) {
      handleFormDataChange({
        ...formData,
        perimetere: "7",
        rayonOptionType: "selectionner", // Set to show "Selectionner..." as default
      });
    } else if (
      formData.lieu &&
      formData.lieu.length !== 1 &&
      formData.perimetere !== null
    ) {
      handleFormDataChange({
        ...formData,
        perimetere: null,
        rayonOptionType: null,
      });
    }
  }, [formData.lieu, formData.perimetere]);

  const [isSearching, setIsSearching] = useState(false);
  const [isCounting, setIsCounting] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);

  useEffect(() => {
    let timer;
    if (isCounting) {
      timer = setTimeout(() => setShowSpinner(true), 500);
    } else {
      setShowSpinner(false);
    }
    return () => clearTimeout(timer);
  }, [isCounting]);

  const [lotsCount, setLotsCount] = useState(null);
  const [programmesCount, setProgrammesCount] = useState(null);

  const debouncedSearchCount = debounce(async (formData) => {
    try {
      setIsCounting(true);
      const response = await dispatch(getSearchResultsCount(formData));
      setLotsCount(response.lotsCount);
      setProgrammesCount(response.programmesCount);
      setIsCounting(false);
    } catch (error) {
      console.error("Error getting search results count:", error);
    }
  }, 1000);

  // Add pagination state for num_lot select
  const [lotPagination, setLotPagination] = useState(createPaginationState(50));

  // Add pagination state for programme select
  const [programmePagination, setProgrammePagination] = useState(
    createPaginationState(50)
  );

  // Add pagination state for lieu select
  const [lieuPagination, setLieuPagination] = useState(
    createPaginationState(30)
  );

  // Add pagination state for promoteur select
  const [promoteurPagination, setPromoteurPagination] = useState(
    createPaginationState(50)
  );

  // Calculate visible options for num_lot
  const visibleLotOptions = useMemo(() => {
    return getVisibleOptions(
      num_lot,
      lotPagination,
      "reference",
      "reference",
      "reference"
    );
  }, [num_lot, lotPagination]);

  // Calculate visible options for programme
  const visibleProgrammeOptions = useMemo(() => {
    return getVisibleOptions(
      programme,
      programmePagination,
      "name",
      "id",
      "name"
    );
  }, [programme, programmePagination]);

  // Calculate visible options for lieu
  const visibleLieuOptions = useMemo(() => {
    const { currentPage, searchTerm, itemsPerPage } = lieuPagination;

    // Filter items based on search term
    const filteredItems =
      lieu?.filter((item) =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
      ) || [];

    // Sort by name length from short to long
    const sortedItems = filteredItems.sort(
      (a, b) => a.name.length - b.name.length
    );

    // Return items up to current page
    const totalItems = currentPage * itemsPerPage;
    return sortedItems.slice(0, totalItems).map((item) => ({
      value: item.id,
      label: item.name,
    }));
  }, [lieu, lieuPagination]);

  // Calculate visible options for promoteur
  const visiblePromoteurOptions = useMemo(() => {
    return getVisibleOptions(
      promoteur,
      promoteurPagination,
      "name",
      "id",
      "name"
    );
  }, [promoteur, promoteurPagination]);

  // Handle loading more items when scrolling to bottom
  const handleLotScrollToBottom = () => {
    handleScrollToBottom(num_lot, lotPagination, setLotPagination, "reference");
  };

  // Handle loading more programme items when scrolling to bottom
  const handleProgrammeScrollToBottom = () => {
    handleScrollToBottom(
      programme,
      programmePagination,
      setProgrammePagination,
      "name"
    );
  };

  // Handle loading more lieu items when scrolling to bottom
  const handleLieuScrollToBottom = () => {
    handleScrollToBottom(lieu, lieuPagination, setLieuPagination, "name");
  };

  // Handle loading more promoteur items when scrolling to bottom
  const handlePromoteurScrollToBottom = () => {
    handleScrollToBottom(
      promoteur,
      promoteurPagination,
      setPromoteurPagination,
      "name"
    );
  };

  // Handle search input change
  const handleLotInputChange = (inputValue) => {
    handleInputChange(inputValue, setLotPagination);
  };

  // Handle programme search input change
  const handleProgrammeInputChange = (inputValue) => {
    handleInputChange(inputValue, setProgrammePagination);
  };

  // Handle lieu search input change
  const handleLieuInputChange = (inputValue) => {
    handleInputChange(inputValue, setLieuPagination);
  };

  // Handle promoteur search input change
  const handlePromoteurInputChange = (inputValue) => {
    handleInputChange(inputValue, setPromoteurPagination);
  };

  const handleSave = async () => {
    // Check if search name is provided
    if (isSearchNameEmpty()) {
      toast.error("Veuillez saisir un nom pour votre recherche");
      return;
    }

    // Filter out empty, null, or empty array fields
    const filterEmptyFields = (obj) => {
      const filtered = {};
      Object.keys(obj).forEach((key) => {
        // Skip rayonOptionType field completely
        if (key === "rayonOptionType") return;

        const value = obj[key];
        // Keep field if it's not empty, null, undefined, or empty array
        if (value !== "" && value !== null && value !== undefined) {
          if (Array.isArray(value)) {
            if (value.length > 0) {
              filtered[key] = value;
            }
          } else {
            filtered[key] = value;
          }
        }
      });
      return filtered;
    };

    const filteredFormData = filterEmptyFields(formData);

    try {
      const response = await mainAxios.post(
        `${process.env.REACT_APP_ENDPOINT_URL}api/search-histories?populate=*`,
        {
          data: {
            ...filteredFormData,
            lotsCount: lotsCount,
            programmesCount: programmesCount,
            user_searchHistory: user.documentId,
          },
        }
      );
      dispatch({
        type: "DEEP_SEARCH_SEARCH_HISTORY",
        payload: response.data.data,
      });
      dispatch(UserData());
      toast.success("Recherche enregistrée avec succès");
      return response.data.data;
    } catch (error) {
      console.error("Error saving search:", error);
    }
  };

  const getPreviousSearch = async (id) => {
    try {
      const response = await mainAxios.get(
        `${process.env.REACT_APP_ENDPOINT_URL}api/search-histories/${id}?populate=*`
      );

      // Get only the fields that are defined in our formData state
      const apiData = response.data.data;
      let filteredData = {};
      // Only copy fields that exist in our formData state
      Object.keys(formData).forEach((key) => {
        if (key in apiData) {
          filteredData[key] = apiData[key];
        }
      });

      setFormData(filteredData);
      const transformedSearch = {
        ...apiData,
        lieu: apiData.lieu ? apiData.lieu.map((location) => location.id) : [],
        programme: apiData.programme
          ? apiData.programme.map((programme) => programme.id)
          : [],
        promoteur: apiData.promoteur
          ? apiData.promoteur.map((promoteur) => promoteur.id)
          : [],
      };
      dispatch(
        openModal({
          title: "Modifier la recherche",
          content: <EditSearchModal searchData={transformedSearch} />,
        })
      );
      // dispatch(closeModal());
    } catch (error) {
      console.error("Error loading previous search:", error);
    }
  };

  const handleSearch = async () => {
    // Check if search name is provided
    if (isSearchNameEmpty()) {
      toast.error("Veuillez saisir un nom pour votre recherche");
      return;
    }

    try {
      setIsSearching(true);
      //deep search

      // Also save the search if user is logged in
      if (user && user.documentId && !isFormEmpty()) {
        const search = await handleSave();

        await dispatch(
          deepSearchFunction(
            formData,
            "search",
            search.documentId,
            search.nomRecherche
          )
        );
      }
    } catch (error) {
      console.error("Error during search:", error);
    } finally {
      setIsSearching(false);

      redirect("/immobilier");
      dispatch(closeModal());
    }
  };

  // Check if all form fields are empty or null
  const isFormEmpty = () => {
    return Object.values(formData).every(
      (value) => value === null || value === "" || value === undefined
    );
  };

  // Check if the search name is empty
  const isSearchNameEmpty = () => {
    return !formData.nomRecherche || formData.nomRecherche.trim() === "";
  };

  // Helper function to render selected values when more than 2 are selected
  const renderSelectedValues = (
    selectedArray,
    dataSource,
    labelKey = "name"
  ) => {
    if (!selectedArray || selectedArray.length < 2) return null;

    const selectedLabels = selectedArray.map((id) => {
      if (dataSource && Array.isArray(dataSource)) {
        const item = dataSource.find((item) => item.id === id);
        return item ? item[labelKey] : id;
      }
      return id;
    });

    return (
      <div className="mt-1">
        <small className="text-muted fs12">
          Sélectionnés: {selectedLabels.join(", ")}
        </small>
      </div>
    );
  };

  // Helper function for static options (no data source needed)
  const renderSelectedStaticValues = (selectedArray, options) => {
    if (!selectedArray || selectedArray.length < 2) return null;

    const selectedLabels = selectedArray.map((value) => {
      const option = options.find((opt) => opt.value === value);
      return option ? option.label : value;
    });

    return (
      <div className="mt-1">
        <small className="text-muted fs12">
          Sélectionnés: {selectedLabels.join(", ")}
        </small>
      </div>
    );
  };

  const handleFormDataChange = (newFormData) => {
    setFormData(newFormData);
    debouncedSearchCount(newFormData);
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <div
          className="spinner-border text-pink"
          role="status"
          style={{ width: "3rem", height: "3rem" }}
        >
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }
  //TO PUT ONLINE AND INFORM CLIENT
  return (
    <div className="d-flex flex-column justify-content-between gap-2">
      <div className="deep-search-dialog">
        <div className="searchDetails ">
          <div className="deep-search-container ">
            {/* DIV A */}
            <div className="divA item">
              <p className="borderBottom pink semibold fs16 pb-3 ps-4">
                Général
              </p>
              <div className="d-flex flex-column px-4 py-3 borderRight h95">
                <div className="d-flex flex-column">
                  <label htmlFor="" className="mb-1">
                    Nom de Recherche *
                  </label>
                  <input
                    type="text"
                    name="nomRecherche"
                    value={formData.nomRecherche || ""}
                    onChange={(e) =>
                      handleFormDataChange({
                        ...formData,
                        nomRecherche: e.target.value,
                      })
                    }
                    required
                  />
                </div>
                <div className="d-flex align-items-center gap10 my-3">
                  <input
                    type="checkbox"
                    checked={formData.missingLotData || false}
                    style={{
                      height: "18px",
                      width: "18px",
                      border: "1px solid #BABABA",
                      borderRadius: "5px",
                    }}
                    onChange={(e) =>
                      handleFormDataChange({
                        ...formData,
                        missingLotData: e.target.checked,
                      })
                    }
                  />
                  <label htmlFor="">Quarantaine (lots cachés du site)</label>
                </div>
                {/* <div className="d-flex flex-column mb-20">
                  <label htmlFor="" className="mb-1">
                    Usage
                  </label>
                  <Select
                    isClearable={true}
                    styles={customStyles}
                    options={[
                      { value: "RP", label: "RP" },
                      { value: "INVEST", label: "INVEST" },
                      { value: "RS", label: "RS" },
                    ]}
                    value={
                      formData.usage
                        ? { value: formData.usage, label: formData.usage }
                        : null
                    }
                    onChange={(selectedOption) =>
                      handleFormDataChange({
                        ...formData,
                        usage: selectedOption ? selectedOption.value : null,
                      })
                    }
                    placeholder="Selectionner..."
                  />
                </div> */}
                <div className="d-flex flex-column mb-20">
                  <label htmlFor="" className="mb-1">
                    Lieu
                  </label>
                  <Select
                    styles={customStyles}
                    isMulti={true}
                    options={visibleLieuOptions}
                    value={getMultiSelectValue(formData.lieu, lieu)}
                    maxMenuHeight={150}
                    onChange={(selectedOptions) =>
                      handleMultiSelectChange(
                        selectedOptions,
                        "lieu",
                        formData,
                        handleFormDataChange
                      )
                    }
                    onMenuScrollToBottom={handleLieuScrollToBottom}
                    onInputChange={handleLieuInputChange}
                    placeholder="Selectionner..."
                    noOptionsMessage={({ inputValue }) =>
                      inputValue
                        ? `Aucun lieu trouvé pour "${inputValue}"`
                        : "Tapez pour rechercher"
                    }
                    isSearchable={true}
                    filterOption={null} // Disable built-in filtering since we handle it manually
                  />
                  {renderSelectedValues(formData.lieu, lieu)}
                </div>
                {/* Conditional perimetere field - only show when lieu is selected */}
                {formData.lieu && formData.lieu.length == 1 && (
                  <div className="d-flex flex-column mb-20">
                    <label htmlFor="" className="mb-1">
                      Rayon (km)
                    </label>
                    <Select
                      isClearable={true}
                      styles={customStyles}
                      options={[
                        { value: "7", label: "Selectionner..." },
                        { value: "5", label: "5 km" },
                        { value: "10", label: "10 km" },
                        { value: "20", label: "20 km" },
                        { value: "30", label: "30 km" },
                        { value: "50", label: "50 km" },
                        { value: "100", label: "100 km" },
                      ]}
                      value={
                        formData.perimetere
                          ? formData.rayonOptionType === "selectionner"
                            ? { value: "7", label: "Selectionner..." }
                            : {
                                value: formData.perimetere,
                                label: `${formData.perimetere} km`,
                              }
                          : { value: "7", label: "Selectionner..." }
                      }
                      onChange={(selectedOption) => {
                        // Special handling for "Selectionner..." option
                        if (
                          selectedOption &&
                          selectedOption.label === "Selectionner..."
                        ) {
                          handleFormDataChange({
                            ...formData,
                            perimetere: "7",
                            rayonOptionType: "selectionner",
                          });
                        } else {
                          handleFormDataChange({
                            ...formData,
                            perimetere: selectedOption?.value || null,
                            rayonOptionType: null,
                          });
                        }
                      }}
                      placeholder="Selectionner..."
                    />
                    {!formData?.perimetere || formData?.perimetere === "7" ? (
                      <label htmlFor="perimetere" className="mb-1">
                        7km (par défaut)
                      </label>
                    ) : null}
                  </div>
                )}
                <div className="d-flex flex-column mb-20">
                  <label htmlFor="" className="mb-1">
                    Dispositif
                  </label>
                  <Select
                    styles={customStyles}
                    isMulti
                    options={
                      dispositif?.map((item) => ({
                        value: item.id,
                        label: item.label,
                      })) || []
                    }
                    value={
                      formData.dispositif && formData.dispositif.length > 0
                        ? dispositif
                            ?.filter((item) =>
                              formData.dispositif.includes(item.id)
                            )
                            .map((item) => ({
                              value: item.id,
                              label: item.label,
                            })) || []
                        : []
                    }
                    onChange={(selectedOptions) => {
                      const selectedIds = selectedOptions
                        ? selectedOptions.map((option) => option.value)
                        : [];
                      handleFormDataChange({
                        ...formData,
                        dispositif: selectedIds,
                      });
                    }}
                    placeholder="Selectionner..."
                  />
                  {renderSelectedValues(
                    formData.dispositif,
                    dispositif,
                    "label"
                  )}
                </div>
                <div className="d-flex flex-column mb-20">
                  <label htmlFor="" className="mb-1">
                    Zone Fiscale
                  </label>
                  <Select
                    styles={customStyles}
                    isMulti
                    options={
                      zone_fiscal?.map((zone) => ({
                        value: zone,
                        label: zone,
                      })) || []
                    }
                    value={
                      formData.zone_fiscale
                        ? zone_fiscal
                            ?.filter((zone) =>
                              formData.zone_fiscale.includes(zone)
                            )
                            .map((zone) => ({ value: zone, label: zone })) || []
                        : []
                    }
                    onChange={(selectedOptions) => {
                      const selectedValues = selectedOptions
                        ? selectedOptions.map((option) => option.value)
                        : [];
                      handleFormDataChange({
                        ...formData,
                        zone_fiscale: selectedValues,
                      });
                    }}
                    placeholder="Selectionner..."
                  />
                  {renderSelectedStaticValues(
                    formData.zone_fiscale,
                    zone_fiscal?.map((zone) => ({
                      value: zone,
                      label: zone,
                    })) || []
                  )}
                </div>
                <div className="d-flex flex-column mb-20">
                  <label htmlFor="" className="mb-1">
                    Statut du lot
                  </label>
                  <Select
                    isClearable={true}
                    styles={customStyles}
                    options={
                      status_lot?.map((item) => ({
                        value: item.disponibilite,
                        label: item.disponibilite,
                      })) || []
                    }
                    value={
                      formData.statutLot
                        ? {
                            value: formData.statutLot,
                            label: formData.statutLot,
                          }
                        : null
                    }
                    onChange={(selectedOption) =>
                      handleFormDataChange({
                        ...formData,
                        statutLot: selectedOption?.value || "",
                      })
                    }
                    placeholder="Selectionner..."
                  />
                </div>
                <div className="d-flex flex-column mb-20">
                  <label htmlFor="" className="mb-1">
                    Nature du mandat
                  </label>
                  <Select
                    isClearable={true}
                    styles={customStyles}
                    options={[
                      { value: "interne", label: "Interne" },
                      { value: "externe", label: "Externe" },
                    ]}
                    value={
                      [
                        { value: "interne", label: "Interne" },
                        { value: "externe", label: "Externe" },
                      ].find((option) => option.value === formData.origine) ||
                      null
                    }
                    onChange={(selectedOptions) =>
                      handleFormDataChange({
                        ...formData,
                        origine: selectedOptions?.value || "",
                      })
                    }
                    placeholder="Selectionner..."
                  />
                </div>
                <div className="d-flex flex-column">
                  <label htmlFor="">Nouveautés depuis le :</label>
                  <input
                    type="date"
                    name="nouveaute_depuis"
                    value={formData.nouveaute_depuis || ""}
                    onChange={(e) =>
                      handleFormDataChange({
                        ...formData,
                        nouveaute_depuis: e.target.value,
                      })
                    }
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #BABABA",
                      borderRadius: "5px",
                      fontSize: "14px",
                    }}
                  />
                </div>
              </div>
            </div>
            {/* DIV B */}
            <div className="divB item">
              <p className="borderBottom pink semibold fs16 pb-3  ps-4">
                Économique
              </p>
              <div className="d-flex flex-column px-4 py-3 borderRight">
                <div className="d-flex flex-column mb-25">
                  <label htmlFor="" className="mb-1">
                    Budget
                  </label>
                  <div className="d-flex align-items-center budget">
                    <span className="greyBadge radiusTL5 radiusBL5">De</span>{" "}
                    <input
                      type="number"
                      placeholder="Min"
                      onWheelCapture={(e) => e.target.blur()}
                      step="1"
                      name="budgetMin"
                      value={formData.budgetMin || ""}
                      onChange={(e) =>
                        handleFormDataChange({
                          ...formData,
                          budgetMin: e.target.value,
                        })
                      }
                      className="radius-0"
                    />
                    <span className="greyBadge">à</span>
                    <input
                      type="number"
                      placeholder="Max"
                      onWheelCapture={(e) => e.target.blur()}
                      step="1"
                      name="budgetMax"
                      value={formData.budgetMax || ""}
                      onChange={(e) =>
                        handleFormDataChange({
                          ...formData,
                          budgetMax: e.target.value,
                        })
                      }
                      className="radius-0"
                    />{" "}
                    <span className="greyBadge radiusTR5 radiusBR5">€</span>
                  </div>
                </div>
                <div className="d-flex flex-column mb-25">
                  <label htmlFor="" className="mb-1">
                    Prix au m2
                  </label>
                  <div className="d-flex align-items-center budget">
                    <span className="greyBadge radiusTL5 radiusBL5">De</span>{" "}
                    <input
                      type="number"
                      placeholder="Min"
                      onWheelCapture={(e) => e.target.blur()}
                      step="0.01"
                      name="prixM2Min"
                      value={formData.prixM2Min || ""}
                      onChange={(e) =>
                        handleFormDataChange({
                          ...formData,
                          prixM2Min: e.target.value,
                        })
                      }
                      className="radius-0"
                    />
                    <span className="greyBadge ">à</span>{" "}
                    <input
                      type="number"
                      placeholder="Max"
                      onWheelCapture={(e) => e.target.blur()}
                      step="0.01"
                      name="prixM2Max"
                      value={formData.prixM2Max || ""}
                      onChange={(e) =>
                        handleFormDataChange({
                          ...formData,
                          prixM2Max: e.target.value,
                        })
                      }
                      className="radius-0"
                    />{" "}
                    <span className="greyBadge radiusTR5 radiusBR5">€</span>
                  </div>
                </div>
                <div className="d-flex flex-row gap-5">
                  <div className="d-flex flex-column mb-25 w50">
                    <label htmlFor="" className="mb-1">
                      Rentabilité
                    </label>
                    <div className="d-flex align-items-center budget">
                      <input
                        type="number"
                        onWheelCapture={(e) => e.target.blur()}
                        placeholder="Min"
                        step="0.01"
                        name="honorairesMax"
                        value={formData.rentabiliteMin || ""}
                        onChange={(e) =>
                          handleFormDataChange({
                            ...formData,
                            rentabiliteMin: e.target.value,
                          })
                        }
                        className="leftBorderedInput"
                        style={{
                          borderTopLeftRadius: 5,
                          borderBottomLeftRadius: 5,
                        }}
                      />{" "}
                      <span className="greyBadge radiusTR5 radiusBR5">%/</span>
                    </div>
                  </div>
                  <div className="d-flex flex-column mb-25 w50">
                    <label htmlFor="" className="mb-1">
                      Honoraires
                    </label>
                    <div className="d-flex align-items-center budget">
                      <input
                        type="number"
                        onWheelCapture={(e) => e.target.blur()}
                        placeholder="Min"
                        step="0.01"
                        name="honorairesMin"
                        value={formData.honorairesMin || ""}
                        onChange={(e) =>
                          handleFormDataChange({
                            ...formData,
                            honorairesMin: e.target.value,
                          })
                        }
                        className="leftBorderedInput"
                      />{" "}
                      <span className="greyBadge radiusTR5 radiusBR5">
                        %/pts
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* DIV C */}
            <div className="divC item d-flex flex-column gap10 ">
              <p className="borderBottom pink semibold fs16 pb-3  ps-4">
                Dernières recherches
              </p>
              <div
                className="d-flex flex-column px-4 py-3 gap10"
                style={{ maxHeight: "300px", overflow: "auto" }}
              >
                {user.search_histories.length > 0 ? (
                  user.search_histories.map((search, index) => {
                    // Extract up to 3 non-empty filters from the search
                    const filters = [];
                    if (search.lieu?.length > 0)
                      filters.push(
                        `Lieu: ${search.lieu
                          .map((location) => location.name)
                          .join(", ")}`
                      );
                    if (search.promoteur?.length > 0)
                      filters.push(
                        `Promoteur: ${search.promoteur
                          .map((promoteur) => promoteur.name)
                          .join(", ")}`
                      );
                    if (search.programmeParent?.length > 0)
                      filters.push(
                        `Programme: ${search.programmeParent
                          .map((programme) => programme.name)
                          .join(", ")}`
                      );
                    if (search.budgetMin || search.budgetMax) {
                      const budget = `Budget: ${search.budgetMin || "0"}€ - ${
                        search.budgetMax || "∞"
                      }€`;
                      filters.push(budget);
                    }
                    if (search.surfaceMin || search.surfaceMax) {
                      const surface = `Surface: ${
                        search.surfaceMin || "0"
                      }m² - ${search.surfaceMax || "∞"}m²`;
                      filters.push(surface);
                    }
                    if (search.statutLot)
                      filters.push(`Statut: ${search.statutLot}`);
                    if (search.type) filters.push(`Type: ${search.type}`);
                    if (search.origine)
                      filters.push(`Origine: ${search.origine}`);

                    if (search.prixM2Min || search.prixM2Max) {
                      const prixM2 = `Prix/m²: ${search.prixM2Min || "0"}€ - ${
                        search.prixM2Max || "∞"
                      }€`;
                      filters.push(prixM2);
                    }
                    if (search.honorairesMin || search.honorairesMax) {
                      const honoraires = `Honoraires: ${
                        search.honorairesMin || "0"
                      }% - ${search.honorairesMax || "∞"}%`;
                      filters.push(honoraires);
                    }
                    if (search.referenceLot)
                      filters.push(`Réf. lot: ${search.referenceLot}`);
                    if (search.espaceTotalMin || search.espaceTotalMax) {
                      const espaceTotal = `Surface totale: ${
                        search.espaceTotalMin || "0"
                      }m² - ${search.espaceTotalMax || "∞"}m²`;
                      filters.push(espaceTotal);
                    }
                    if (search.typologieLot)
                      filters.push(`Typologie: ${search.typologieLot}`);
                    if (search.etage) filters.push(`Étage: ${search.etage}`);

                    // Get only the first 3 filters
                    const displayFilters = filters.slice(0, 3);

                    return (
                      <div
                        key={index}
                        className="d-flex flex-column gap10 history_search_unit bg-grey radius5 px-2 py-3"
                        onClick={() => {
                          getPreviousSearch(search.documentId);
                        }}
                      >
                        <p className="fs16 semibold text-capitalize">
                          {search.nomRecherche ? (
                            search.nomRecherche
                          ) : (
                            <>
                              Recherche crée le: <br />
                              {moment(search.createdAt).format("DD/MM/YYYY")}
                            </>
                          )}
                        </p>
                        {search.nomRecherche && (
                          <p className="fs12 regular text-dark-grey mb-1">
                            Crée le:{" "}
                            {moment(search.createdAt).format("DD/MM/YYYY")}
                          </p>
                        )}
                        {displayFilters.length > 0 && (
                          <p className="fs14 regular text-dark-grey">
                            {displayFilters.join(", ")}
                          </p>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <p className="fs16 semibold text-capitalize">
                    Aucune recherche antérieure
                  </p>
                )}
                {/*   <SearchHistoryBox /> */}
              </div>
            </div>
            {/* DIV D */}
            <div className="divD item">
              <p className="pink semibold fs16 pb-3 ps-4 mt-3">Lot</p>
              <div className="d-flex flex-column px-4 py-3 borderTop gap30 ">
                <div className="d-flex gap30">
                  <div className="w70">
                    <div className="d-flex gap30">
                      <div className="d-flex flex-column w50 mb-25">
                        <label htmlFor="">Référence du lot</label>
                        <Select
                          styles={customStyles}
                          isMulti
                          options={visibleLotOptions}
                          value={
                            formData.referenceLot &&
                            formData.referenceLot.length > 0
                              ? formData.referenceLot.map((ref) => ({
                                  value: ref,
                                  label: ref,
                                }))
                              : []
                          }
                          onChange={(selectedOptions) => {
                            const selectedReferences = selectedOptions
                              ? selectedOptions.map((option) => option.value)
                              : [];
                            handleFormDataChange({
                              ...formData,
                              referenceLot: selectedReferences,
                            });
                          }}
                          onMenuScrollToBottom={handleLotScrollToBottom}
                          onInputChange={handleLotInputChange}
                          placeholder="Selectionner..."
                          noOptionsMessage={({ inputValue }) =>
                            inputValue
                              ? `Aucune référence trouvée pour "${inputValue}"`
                              : "Tapez pour rechercher"
                          }
                          isSearchable={true}
                          filterOption={null}
                        />
                        {renderSelectedValues(
                          formData.referenceLot,
                          num_lot,
                          "reference"
                        )}
                      </div>

                      <div className="d-flex flex-column w50 mb-25">
                        <label htmlFor="">Type</label>
                        <Select
                          styles={customStyles}
                          isMulti
                          options={[
                            { value: "apartment", label: "Appartement" },
                            { value: "house", label: "Maison" },
                          ]}
                          value={
                            formData.type
                              ? [
                                  { value: "apartment", label: "Appartement" },
                                  { value: "house", label: "Maison" },
                                ].filter((option) =>
                                  formData.type.includes(option.value)
                                )
                              : []
                          }
                          onChange={(selectedOptions) => {
                            const selectedValues = selectedOptions
                              ? selectedOptions.map((option) => option.value)
                              : [];
                            handleFormDataChange({
                              ...formData,
                              type: selectedValues,
                            });
                          }}
                          placeholder="Selectionner..."
                        />
                        {renderSelectedStaticValues(formData.type, [
                          { value: "apartment", label: "Appartement" },
                          { value: "house", label: "Maison" },
                        ])}
                      </div>
                    </div>
                    <div className="d-flex">
                      <div className="d-flex flex-column mb-25 w100">
                        <label htmlFor="" className="mb-1">
                          surface
                        </label>
                        <div className="d-flex align-items-center">
                          <span className="greyBadge radiusTL5 radiusBL5">
                            De
                          </span>{" "}
                          <input
                            type="number"
                            onWheelCapture={(e) => e.target.blur()}
                            placeholder="Min"
                            step="0.01"
                            name="surfaceMin"
                            value={formData.surfaceMin || ""}
                            onChange={(e) =>
                              handleFormDataChange({
                                ...formData,
                                surfaceMin: e.target.value,
                              })
                            }
                            className="radius-0"
                          />
                          <span className="greyBadge ">à</span>{" "}
                          <input
                            type="number"
                            onWheelCapture={(e) => e.target.blur()}
                            placeholder="Max"
                            step="0.01"
                            name="surfaceMax"
                            value={formData.surfaceMax || ""}
                            onChange={(e) =>
                              handleFormDataChange({
                                ...formData,
                                surfaceMax: e.target.value,
                              })
                            }
                            className="radius-0"
                          />{" "}
                          <span className="greyBadge radiusTR5 radiusBR5">
                            m²
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="d-flex">
                      <div className="d-flex flex-column mb-25 w100">
                        <label htmlFor="" className="mb-1">
                          Surface Pondérée (Totale)
                        </label>
                        <div className="d-flex align-items-center">
                          <span className="greyBadge radiusTL5 radiusBL5">
                            De
                          </span>{" "}
                          <input
                            type="number"
                            onWheelCapture={(e) => e.target.blur()}
                            step="0.01"
                            placeholder="Min"
                            name="espaceTotalMin"
                            value={formData.espaceTotalMin || ""}
                            onChange={(e) =>
                              handleFormDataChange({
                                ...formData,
                                espaceTotalMin: e.target.value,
                              })
                            }
                            className="radius-0"
                          />
                          <span className="greyBadge">à</span>
                          <input
                            type="number"
                            onWheelCapture={(e) => e.target.blur()}
                            step="0.01"
                            placeholder="Max"
                            name="espaceTotalMax"
                            value={formData.espaceTotalMax || ""}
                            onChange={(e) =>
                              handleFormDataChange({
                                ...formData,
                                espaceTotalMax: e.target.value,
                              })
                            }
                            className="radius-0"
                          />{" "}
                          <span className="greyBadge radiusTR5 radiusBR5">
                            m²
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="d-flex flex-column w30">
                    <div className="d-flex flex-column mb-25">
                      <label htmlFor="">Typologie</label>
                      <Select
                        styles={customStyles}
                        isMulti
                        options={[
                          { value: "t1", label: "T1" },
                          { value: "t2", label: "T2" },
                          { value: "t3", label: "T3" },
                          { value: "t4", label: "T4" },
                          { value: "t5", label: "T5" },
                          { value: "t6", label: "T6" },
                          { value: "t7", label: "T7" },
                          { value: "t8", label: "T8" },
                          { value: "t9", label: "T9" },
                          { value: "t10", label: "T10" },
                        ]}
                        value={
                          formData.typologieLot
                            ? [
                                { value: "t1", label: "T1" },
                                { value: "t2", label: "T2" },
                                { value: "t3", label: "T3" },
                                { value: "t4", label: "T4" },
                                { value: "t5", label: "T5" },
                                { value: "t6", label: "T6" },
                                { value: "t7", label: "T7" },
                                { value: "t8", label: "T8" },
                                { value: "t9", label: "T9" },
                                { value: "t10", label: "T10" },
                              ].filter((option) =>
                                formData.typologieLot.includes(option.value)
                              )
                            : []
                        }
                        onChange={(selectedOptions) => {
                          const selectedValues = selectedOptions
                            ? selectedOptions.map((option) => option.value)
                            : [];
                          handleFormDataChange({
                            ...formData,
                            typologieLot: selectedValues,
                          });
                        }}
                        placeholder="Selectionner..."
                      />
                      {renderSelectedStaticValues(formData.typologieLot, [
                        { value: "t1", label: "T1" },
                        { value: "t2", label: "T2" },
                        { value: "t3", label: "T3" },
                        { value: "t4", label: "T4" },
                        { value: "t5", label: "T5" },
                        { value: "t6", label: "T6" },
                        { value: "t7", label: "T7" },
                        { value: "t8", label: "T8" },
                        { value: "t9", label: "T9" },
                        { value: "t10", label: "T10" },
                      ])}
                    </div>
                    <div className="d-flex flex-column mb-25">
                      <label htmlFor="">Étage</label>
                      <Select
                        styles={customStyles}
                        isMulti
                        options={[
                          { value: "0", label: "0" },
                          { value: "1", label: "1" },
                          { value: "2", label: "2" },
                          { value: "3", label: "3" },
                          { value: "4", label: "4" },
                          { value: "5", label: "5" },
                          { value: "6", label: "6" },
                          { value: "7", label: "7" },
                          { value: "8", label: "8" },
                          { value: "9", label: "9" },
                          { value: "10", label: "10" },
                          { value: "11", label: "11" },
                          { value: "12", label: "12" },
                          { value: "13", label: "13" },
                          { value: "14", label: "14" },
                          { value: "15", label: "15" },
                          { value: "16", label: "16" },
                          { value: "17", label: "17" },
                          { value: "18", label: "18" },
                          { value: "19", label: "19" },
                          { value: "20", label: "20" },
                        ]}
                        value={
                          formData.etage
                            ? [
                                { value: "0", label: "0" },
                                { value: "1", label: "1" },
                                { value: "2", label: "2" },
                                { value: "3", label: "3" },
                                { value: "4", label: "4" },
                                { value: "5", label: "5" },
                                { value: "6", label: "6" },
                                { value: "7", label: "7" },
                                { value: "8", label: "8" },
                                { value: "9", label: "9" },
                                { value: "10", label: "10" },
                                { value: "11", label: "11" },
                                { value: "12", label: "12" },
                                { value: "13", label: "13" },
                                { value: "14", label: "14" },
                                { value: "15", label: "15" },
                                { value: "16", label: "16" },
                                { value: "17", label: "17" },
                                { value: "18", label: "18" },
                                { value: "19", label: "19" },
                                { value: "20", label: "20" },
                              ].filter((option) =>
                                formData.etage.includes(option.value)
                              )
                            : []
                        }
                        onChange={(selectedOptions) => {
                          const selectedValues = selectedOptions
                            ? selectedOptions.map((option) => option.value)
                            : [];
                          handleFormDataChange({
                            ...formData,
                            etage: selectedValues,
                          });
                        }}
                        placeholder="Selectionner..."
                      />
                      {renderSelectedStaticValues(formData.etage, [
                        { value: "0", label: "0" },
                        { value: "1", label: "1" },
                        { value: "2", label: "2" },
                        { value: "3", label: "3" },
                        { value: "4", label: "4" },
                        { value: "5", label: "5" },
                        { value: "6", label: "6" },
                        { value: "7", label: "7" },
                        { value: "8", label: "8" },
                        { value: "9", label: "9" },
                        { value: "10", label: "10" },
                        { value: "11", label: "11" },
                        { value: "12", label: "12" },
                        { value: "13", label: "13" },
                        { value: "14", label: "14" },
                        { value: "15", label: "15" },
                        { value: "16", label: "16" },
                        { value: "17", label: "17" },
                        { value: "18", label: "18" },
                        { value: "19", label: "19" },
                        { value: "20", label: "20" },
                      ])}
                    </div>

                    <div className="d-flex flex-column mb-25">
                      <label htmlFor="">Annexe</label>
                      <Select
                        styles={customStyles}
                        isMulti
                        options={[
                          {
                            value: "Parking exterieur",
                            label: "Parking exterieur",
                          },
                          {
                            value: "Parking sous-sol",
                            label: "parking sous-sol",
                          },
                          { value: "Garage", label: "Garage" },
                          { value: "Cave", label: "Cave" },
                        ]}
                        value={
                          formData.annexe && formData.annexe.length > 0
                            ? [
                                {
                                  value: "Parking exterieur",
                                  label: "Parking exterieur",
                                },
                                {
                                  value: "Parking sous-sol",
                                  label: "Parking sous-sol",
                                },
                                { value: "Garage", label: "Garage" },
                                { value: "Cave", label: "Cave" },
                              ].filter((option) =>
                                formData.annexe.includes(option.value)
                              )
                            : []
                        }
                        onChange={(selectedOptions) => {
                          const selectedValues = selectedOptions
                            ? selectedOptions.map((option) => option.value)
                            : [];
                          handleFormDataChange({
                            ...formData,
                            annexe: selectedValues,
                          });
                        }}
                        placeholder="Selectionner..."
                      />
                      {renderSelectedStaticValues(formData.annexe, [
                        {
                          value: "Parking exterieur",
                          label: "Parking exterieur",
                        },
                        {
                          value: "Parking sous-sol",
                          label: "Parking sous-sol",
                        },
                        { value: "Garage", label: "Garage" },
                        { value: "Cave", label: "Cave" },
                      ])}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* DIV E */}
            <div className="divE item">
              <p className="pink semibold fs16 pb-3 ps-4 mt-3">Le programme</p>
              <div className="d-flex px-4 py-3 borderTop gap30 ">
                <div className="d-flex flex-column w25">
                  <label htmlFor="">Programme</label>
                  <Select
                    styles={customStyles}
                    isMulti
                    options={visibleProgrammeOptions}
                    value={
                      formData.programme && formData.programme.length > 0
                        ? programme
                            ?.filter((item) =>
                              formData.programme.includes(item.id)
                            )
                            .map((item) => ({
                              value: item.id,
                              label: item.name,
                            })) || []
                        : []
                    }
                    onChange={(selectedOptions) => {
                      const selectedIds = selectedOptions
                        ? selectedOptions.map((option) => option.value)
                        : [];
                      handleFormDataChange({
                        ...formData,
                        programme: selectedIds,
                      });
                    }}
                    onMenuScrollToBottom={handleProgrammeScrollToBottom}
                    onInputChange={handleProgrammeInputChange}
                    placeholder="Selectionner..."
                    noOptionsMessage={({ inputValue }) =>
                      inputValue
                        ? `Aucun programme trouvé pour "${inputValue}"`
                        : "Tapez pour rechercher"
                    }
                    isSearchable={true}
                    filterOption={null} // Disable built-in filtering since we handle it manually
                  />
                  {renderSelectedValues(formData.programme, programme)}
                </div>
                <div className="d-flex flex-column w25">
                  <label htmlFor="">Promoteur</label>
                  <Select
                    styles={customStyles}
                    isMulti
                    options={visiblePromoteurOptions}
                    value={
                      formData.promoteur && formData.promoteur.length > 0
                        ? promoteur
                            ?.filter((item) =>
                              formData.promoteur.includes(item.id)
                            )
                            .map((item) => ({
                              value: item.id,
                              label: item.name,
                            })) || []
                        : []
                    }
                    onChange={(selectedOptions) => {
                      const selectedIds = selectedOptions
                        ? selectedOptions.map((option) => option.value)
                        : [];
                      handleFormDataChange({
                        ...formData,
                        promoteur: selectedIds,
                      });
                    }}
                    onMenuScrollToBottom={handlePromoteurScrollToBottom}
                    onInputChange={handlePromoteurInputChange}
                    placeholder="Selectionner..."
                    noOptionsMessage={({ inputValue }) =>
                      inputValue
                        ? `Aucun promoteur trouvé pour "${inputValue}"`
                        : "Tapez pour rechercher"
                    }
                    isSearchable={true}
                    filterOption={null} // Disable built-in filtering since we handle it manually
                  />
                  {renderSelectedValues(formData.promoteur, promoteur)}
                </div>
                <div className="d-flex flex-column w25">
                  <label htmlFor="">Label</label>
                  <Select
                    styles={customStyles}
                    isMulti
                    options={[
                      { value: "RT 2012", label: "RT 2012" },
                      { value: "RE 2020", label: "RE 2020" },
                      { value: "Autre", label: "Autre" },
                    ]}
                    value={
                      formData.label && formData.label.length > 0
                        ? [
                            { value: "RT 2012", label: "RT 2012" },
                            { value: "RE 2020", label: "RE 2020" },
                            { value: "Autre", label: "Autre" },
                          ].filter((option) =>
                            formData.label.includes(option.value)
                          )
                        : []
                    }
                    onChange={(selectedOptions) => {
                      const selectedValues = selectedOptions
                        ? selectedOptions.map((option) => option.value)
                        : [];
                      handleFormDataChange({
                        ...formData,
                        label: selectedValues,
                      });
                    }}
                    placeholder="Selectionner..."
                  />
                  {renderSelectedStaticValues(formData.label, [
                    { value: "RT 2012", label: "RT 2012" },
                    { value: "RE 2020", label: "RE 2020" },
                    { value: "Autre", label: "Autre" },
                  ])}
                </div>
                <div className=" mt-3 gap10 w25">
                  <div className="d-flex align-items-center gap10">
                    <input
                      type="checkbox"
                      style={{
                        height: "18px",
                        width: "18px",
                        border: "1px solid #BABABA",
                        borderRadius: "5px",
                      }}
                      checked={formData.allotement || false}
                      onChange={(e) =>
                        handleFormDataChange({
                          ...formData,
                          allotement: e.target.checked,
                        })
                      }
                    />
                    <label htmlFor="">En exclusivité</label>
                  </div>
                  <div className="d-flex align-items-center gap10">
                    <input
                      type="checkbox"
                      style={{
                        height: "18px",
                        width: "18px",
                        border: "1px solid #BABABA",
                        borderRadius: "5px",
                      }}
                      checked={formData.offre_speciale || false}
                      onChange={(e) =>
                        handleFormDataChange({
                          ...formData,
                          offre_speciale: e.target.checked,
                        })
                      }
                    />
                    <label htmlFor="">Offre spéciale</label>
                  </div>
                  <div className="d-flex align-items-center gap10">
                    <input
                      type="checkbox"
                      style={{
                        height: "18px",
                        width: "18px",
                        border: "1px solid #BABABA",
                        borderRadius: "5px",
                      }}
                      checked={formData.programmeHidden || false}
                      onChange={(e) =>
                        handleFormDataChange({
                          ...formData,
                          programmeHidden: e.target.checked,
                        })
                      }
                    />
                    <label htmlFor="">Programme masqué</label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="">
        {showSpinner ? (
          <div className="w100 d-flex justify-content-center">
            <div
              className="spinner-border spinner-border-sm"
              role="status"
            ></div>
          </div>
        ) : (
          <>
            {lotsCount !== null && (
              <div className="d-flex justify-content-center mt-3">
                <p className="fs14 text-dark-grey">
                  {lotsCount > 0
                    ? `${lotsCount} lot${lotsCount > 1 ? "s" : ""} trouvé${
                        lotsCount > 1 ? "s" : ""
                      }`
                    : "Aucun lot trouvé"}
                </p>
              </div>
            )}
            {programmesCount !== null && (
              <div className="d-flex justify-content-center">
                <p className="fs14 text-dark-grey">
                  {programmesCount > 0
                    ? `${programmesCount} programme${
                        programmesCount > 1 ? "s" : ""
                      }`
                    : "Aucun programme trouvé"}
                </p>
              </div>
            )}
          </>
        )}

        <div className="d-flex justify-content-center gap30 mt-4">
          <button
            className="pink fs16 pink radius5 bg-white outline-0 borderPink px-30 py-10 applySave"
            onClick={handleSave}
            disabled={isSearching || isFormEmpty()}
          >
            Enregistrer ma recherche
          </button>
          <button
            className="white fs16 pink radius5 bg-pink outline-0 borderPink px-30 py-10 applySearch"
            onClick={handleSearch}
            disabled={isSearching || isFormEmpty()}
          >
            {isSearching ? (
              <div className="spinner-border text-light" role="status">
                <span className="visually-hidden">Chargement...</span>
              </div>
            ) : (
              "Afficher les résultats"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeepSearch;
